'use client';
import {
  HotelAvailabilityStatusType,
  HotelDetailViewModel,
  LoyaltyType,
  PackageHotelDetailViewModel,
  RoomVariationViewModel,
} from '@api/v3';
import { AlertType } from '@components/views/hotel/detail/components/room-selection/components/Alert';

import findDiffDates from './findDiffDates';
import {
  removeSafeLocalStorageItem,
  setSafeLocalStorageItem,
} from './localStorage/getSafeLocalStorage';

export const formatArrayToString = (param: string | string[]) =>
  Array.isArray(param) ? param[0] : param;

interface AvailableStatus {
  button: string;
  alertType?: AlertType;
}

export const getAvailableStatus = (status?: string): AvailableStatus => {
  switch (status) {
    case HotelAvailabilityStatusType.AVAILABLE:
      return {
        button: 'Oda Seç',
      };
    case HotelAvailabilityStatusType.ASK_SELL_PLAN:
    case HotelAvailabilityStatusType.TOO_LATE:
    case HotelAvailabilityStatusType.NOT_IN_STOCK:
      return {
        button: 'Yeniden Ara',
        alertType: AlertType.SECONDARY,
      };
    case HotelAvailabilityStatusType.SHORT_ACCOMMODATION:
    case HotelAvailabilityStatusType.MIN_STAY_NOT_SATISFIED:
    case HotelAvailabilityStatusType.SHORT_ACCOMMODATION_AND_MIN_STAY_TO_ASK_SELL_FALSE:
      return {
        button: 'Tarih Seç',
        alertType: AlertType.PRIMARY,
      };

    case HotelAvailabilityStatusType.CLOSED_QUOTA:
    case HotelAvailabilityStatusType.N_A:
    case HotelAvailabilityStatusType.MAX_PEOPLE_EXCEEDED:
    case HotelAvailabilityStatusType.ACCOMMODATION_TOO_LONG:
    case HotelAvailabilityStatusType.RESTRICTED:
      return {
        button: 'Yeniden Ara',
        alertType: AlertType.PRIMARY,
      };

    default:
      return {
        button: 'Oda Seç',
      };
  }
};

interface GetRoomInformation {
  adultNumber?: number;
  childNumber?: number;
  childDates?: string[];
  dateDifference?: number;
  message: string;
}

export const getRoomInformations = (
  roomInfo?: string,
  startDate?: string,
  endDate?: string
): GetRoomInformation => {
  let adultNumber, dateDifference, childDates;

  if (startDate && endDate) {
    const a = new Date(startDate);
    const b = new Date(endDate);
    const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
    const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());

    dateDifference = Math.floor((utc2 - utc1) / (1000 * 60 * 60 * 24));
  }
  if (roomInfo) {
    adultNumber = roomInfo
      .toString()
      ?.split('_')
      ?.map((item) => (item.includes('-') ? '' : item))
      .reduce((a, b) => Number(a) + Number(b), 0);

    childDates = roomInfo
      .toString()
      ?.split('_')
      ?.map((item) => (item.includes('-') ? item : ''))
      .filter((item) => item !== '');
  }

  return {
    adultNumber: adultNumber,
    childNumber: childDates ? childDates.length : 0,
    childDates: childDates,
    dateDifference: dateDifference,
    message: `${adultNumber ? adultNumber + ' yetişkin' : ''} ${
      childDates && childDates.length ? childDates.length + ' çocuk' : ''
    } ${dateDifference ? dateDifference + ' gece' : ''}`,
  } as GetRoomInformation;
};

export const typeToName = (type?: string): string => {
  switch (type) {
    case LoyaltyType.CHIPPIN:
      return 'Chippin';
    case LoyaltyType.HOPI_BOOSTER:
      return 'Hopi Booster';
    case LoyaltyType.HOPI_KAZANIM:
      return 'Hopi Kazanım';
    case LoyaltyType.KOC_AILEM:
      return 'KoçAilem';
    case LoyaltyType.PARO:
      return 'Paro';
    case LoyaltyType.SETUR_HEDIYE_PUAN:
      return 'Setur Hediye Puan';
    case LoyaltyType.TANI_HEDIYE_KART:
      return 'Tanı Hediye Kart';
    case LoyaltyType.TAV:
      return 'Tav';
    case LoyaltyType.TURK_TELEKOM:
      return 'Türk Telekom';
    case LoyaltyType.TURK_TELEKOM_PRIME:
      return 'Türk Telekom Prime';
    case LoyaltyType.WORLD_PUAN:
      return 'World Puan';
    case LoyaltyType.ZUBIZU:
      return 'Zubizu';

    default:
      return '';
  }
};

export const updateArrayWithoutMutating = (
  data: any[],
  newProperty: any,
  id: string | number
) => {
  const objectIndex = data.findIndex((obj) => obj.id === id);

  const updatedObj = { ...data[objectIndex], ...newProperty };

  const updatedData = [
    ...data.slice(0, objectIndex),
    updatedObj,
    ...data.slice(objectIndex + 1),
  ];

  return updatedData;
};

export const getPeopleInformations = (
  adultCount?: number,
  childCount?: number,
  nightCount?: number,
  roomCount?: number
) => {
  return `${roomCount ? `${roomCount} oda, ` : ''}${
    adultCount ? `${adultCount} yetişkin` : ''
  }${childCount ? `, ${childCount} çocuk` : ''}${
    nightCount ? `, ${nightCount} gece` : ''
  }`;
};

export const getLocations = (
  region?: string,
  city?: string,
  country?: string
) => {
  return `${region ? `${region}, ` : ''}${city ? `${city}/` : ''}${
    country ? country : ''
  }`;
};

export const getUrlParams = (search: string) =>
  `${search}?`
    .split('?')[1]
    .split('&')
    .reduce((params: Record<string, unknown>, pair: string) => {
      const [key, value] = `${pair}=`.split('=').map(decodeURIComponent);

      return key.length > 0 ? { ...params, [key]: value } : params;
    }, {});

export const removeStorageItems = (keys: string[]) =>
  keys.forEach((key) => removeSafeLocalStorageItem(key));

type HotelDetailChildAndVariationsType = {
  variations: { variation: RoomVariationViewModel; boardType: string }[];
  hasChildFreeStatus: boolean;
};

export const getHotelDetailChildAndVariationsInfo = (
  hotelDetail: HotelDetailViewModel | PackageHotelDetailViewModel
): HotelDetailChildAndVariationsType => {
  let minPrice = Number.MAX_SAFE_INTEGER;
  let maxPrice = 0;
  let variationWithMinPrice;
  let variationWithMaxPrice;
  let hasChildFreeStatus = false;
  hotelDetail.rooms?.forEach((room) =>
    room.details?.forEach((detail) =>
      detail.informations?.forEach((info) => {
        if (info.childWithoutPriceCount) {
          hasChildFreeStatus = true;
        }
        info.variations?.forEach((variation) => {
          const price = variation.totalSellingPrice;
          if (price !== undefined) {
            if (price > maxPrice) {
              maxPrice = price;
              variationWithMaxPrice = {
                variation,
                boardType: detail.roomInfo?.boardType,
              };
            }
            if (price < minPrice) {
              minPrice = price;
              variationWithMinPrice = {
                variation,
                boardType: detail.roomInfo?.boardType,
              };
            }
          }
        });
      })
    )
  );

  return {
    variations:
      variationWithMinPrice && variationWithMaxPrice
        ? [variationWithMinPrice, variationWithMaxPrice]
        : [],
    hasChildFreeStatus,
  };
};

export const getNightCount = (
  checkInDate: string | string[] | undefined,
  checkOutDate: string | string[] | undefined
) => {
  return checkInDate && checkOutDate
    ? findDiffDates(String(checkInDate), String(checkOutDate))
    : undefined;
};

export const getCurrencySymbol = (currencyCode = '') => {
  switch (currencyCode?.toLowerCase()) {
    case 'tl':
    case '₺':
      return '₺';
    case 'usd':
    case '$':
      return '$';
    case 'eur':
    case '€':
      return '€';
    case 'gbp':
    case '£':
      return '£';
    case 'puan':
      return 'Puan';
    default:
      return '₺';
  }
};
export const SEARCH_LENGTH = 2;

// ===========================================
// Filter and set href
// ===========================================
export const handleFilterAndUpdateUrl = (
  props: {
    [key: string]: string | number | undefined;
  } = { slug: '' }
) => {
  const { slug, ...params } = props;

  const url = Object.entries(params)
    .map(([key, val]) => {
      if (key === 'room') return val;
      return `${key}=${val}`;
    })
    .join('&');

  return `${slug || ''}${url ? `?${url}` : ''}`;
};
export const clearStorageLogout = () => {
  removeStorageItems([
    'userInfo',
    'loyaltyUserInfo',
    'token',
    'tokenExpiration',
    'loyalties',
    'userId',
    'activePaymentMethod',
  ]);

  setSafeLocalStorageItem('refreshTabs', JSON.stringify(Date.now())); // Logout for SSO accounts
};

export const formatPhoneNumber = (phoneNumber = '') => {
  const firstTwoChars = phoneNumber?.slice(0, 4);
  const lastTwoChars = phoneNumber?.slice(-4);
  const slicedPhoneNumber = phoneNumber?.slice(0, -8);
  const hashedPhoneNumber = slicedPhoneNumber.replace(/./g, '*');
  const result = `${firstTwoChars}${hashedPhoneNumber}${lastTwoChars}`;
  return result;
};

export const toLocalePoint = (point: number, local = 'tr-TR') => {
  return point.toLocaleString(local);
};

export const secondsToMinutesAndSeconds = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  const formattedMinutes = minutes < 10 ? '0' + minutes : minutes;
  const remainingSecondsFormatted =
    remainingSeconds < 10 ? '0' + remainingSeconds : remainingSeconds;
  return `${formattedMinutes}:${remainingSecondsFormatted}`;
};
